import cn from "@/v2-ui/utils/utils.cn"

export const STACKED_ICON_SIZE = 14
export const DEFAULT_ICON_SIZE = 13

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "ghost-no-hover"
  | "link"
  | "destructive"
  | "success"
  | "warning"
  | "suggested"

export type ButtonSize = "tiny" | "xs" | "sm" | "md" | "lg" | "xl" | "fit"

/** Label/icon placement inside a full-width button (see Button.align). */
export type ButtonAlign = "center" | "start" | "between"

/** Deprecated v2-ui-era size, kept as an alias so callsites never break. */
const SIZE_ALIASES: Partial<Record<ButtonSize, ButtonSize>> = {
  fit: "tiny",
}

/**
 * Assembles the class list for one button. The classes themselves live in
 * button.css (plain selectors, normal single-class specificity): callsite
 * utilities override them by cascade, no !important needed. Stacked
 * buttons carry their own layout instead of a size class.
 */
export function getButtonClasses(options: {
  variant: ButtonVariant
  size: ButtonSize
  isStacked?: boolean
  isIconOnly?: boolean
  align?: ButtonAlign
}) {
  const { variant, size, isStacked, isIconOnly, align } = options
  const resolvedSize = SIZE_ALIASES[size] ?? size

  return cn(
    "bo-button",
    `bo-button--${variant}`,
    isStacked && "bo-button--stacked",
    !isStacked && `bo-button--${resolvedSize}`,
    !isStacked && isIconOnly && "bo-button--icon-only",
    align && align !== "center" && `bo-button--align-${align}`,
  )
}

/**
 * For buttons sitting NEXT TO a default-size Input/Textarea: pins the
 * button to the field's own height (h-9, rem-based like the field, so both
 * follow the font-size setting) so the pair reads as one control. Plain
 * utilities — they beat the css classes by cascade.
 */
export const INPUT_ALIGNED_BUTTON_CLASSES = "h-9 py-0"
